import React, {
  useImperativeHandle,
  useEffect,
  useState,
  forwardRef,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import SignatureCanvas from 'react-signature-canvas';
import ExpandIcon from './icons/ExpandIcon';
import i18n from '../services/i18n';

// Use forwardRef to forward the ref
const SignatureContainer = forwardRef((props, ref) => {
  const {
    initialSignature,
    penColor,
    backgroundColor,
    showClear,
    showExpand,
    onExpand,
    disabled,
  } = props;
  // No need for another ref here, just use the forwarded ref
  const signatureRef = useRef(null);
  const [touched, setTouched] = useState(!!initialSignature || false);

  useEffect(() => {
    // Set the initial signature if it exists
    if (initialSignature && signatureRef.current) {
      signatureRef.current.fromDataURL(initialSignature);
    }
  }, [initialSignature, ref]);

  const clearSignature = () => {
    setTouched(false);
    signatureRef.current.clear();
  };

  const handleMouseDown = () => {
    if (!touched) {
      setTouched(true);
    }
  };

  const handleExpand = () => {
    if (signatureRef.current) {
      onExpand(signatureRef.current.toDataURL());
    }
  };

  const saveSignature = () => signatureRef?.current.toDataURL();

  const checkIfEmpty = () =>
    signatureRef.current && signatureRef.current.isEmpty() && !touched;

  // Expose methods through the forwarded ref
  useImperativeHandle(ref, () => ({
    clearSignature,
    saveSignature,
    checkIfEmpty,
    fromDataURL: signatureRef.current ? signatureRef.current.fromDataURL : null,
    toDataURL: signatureRef.current ? signatureRef.current.toDataURL : null,
  }));

  useEffect(() => {
    if (signatureRef.current) {
      if (disabled) {
        signatureRef.current.off();
      }
    }
  }, [disabled]);

  return (
    <div className="signature-container">
      <div className="container">
        {showExpand ? (
          <button className="expand-btn" type="button" onClick={handleExpand}>
            <ExpandIcon />
          </button>
        ) : null}
        <SignatureCanvas
          ref={signatureRef}
          penColor={penColor}
          backgroundColor={backgroundColor}
          canvasProps={{
            onMouseDown: handleMouseDown,
            pointerEvents: 'none',
          }}
          disabled={disabled}
        />
        {showClear && touched ? (
          <button
            className="btn-clear-signature"
            type="button"
            onClick={clearSignature}
          >
            {i18n.__('clear')}
          </button>
        ) : null}
      </div>
    </div>
  );
});

SignatureContainer.propTypes = {
  penColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  initialSignature: PropTypes.string,
  showClear: PropTypes.bool,
  showExpand: PropTypes.bool,
  onExpand: PropTypes.func,
  disabled: PropTypes.func,
};

SignatureContainer.defaultProps = {
  backgroundColor: '#FAFAFD',
  penColor: '#000000',
  initialSignature: '',
  showClear: false,
  showExpand: true,
  onExpand: null,
  disabled: false,
};

export default SignatureContainer;
